import {
  useAuth,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useTransferConsultantCandidateData } from '@/hooks/consultant/useTransferConsultantCandidateData';
import { useGetMyProfile } from '@/hooks/my-profile/useGetMyProfile';
import { useGetZorGeneralSetting } from '@/hooks/zor-general-setting/useGetZorGeneralSetting';
import { MainLayout } from '@/layouts/MainLayout';
import { DashboardAskAimei } from '@/pages/dashboard/ask-aimei';
import { DashboardDataCenter } from '@/pages/dashboard/data-center';
import { DashboardFinancialOverview } from '@/pages/dashboard/financial-overview/FinancialOverview';
import LeadProfile from '@/pages/dashboard/lead-profile';
import { DashboardLibrary } from '@/pages/dashboard/library';
import { DashboardProfile } from '@/pages/dashboard/profile';
import { DashboardTerritories } from '@/pages/dashboard/territories';
import ZeeProfile from '@/pages/dashboard/zee-profile';
import logger from '@/services/logger.service';

export const DashboardRoutes = () => {
  // custom hooks
  const { getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { isLoaded, userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true, keepPreviousData: true },
  });

  // Load ZOR general settings (including brand colors)
  useGetZorGeneralSetting();

  const { result: myProfileResult, isSuccess: myProfileLoaded } =
    useGetMyProfile();
  const { mutate: transferCandidateData, isSuccess: transferSuccess } =
    useTransferConsultantCandidateData();

  // constants
  const TOKEN_TEMPLATE = 'ff-backend-new' as const;
  const TOKEN_STORAGE_KEY = 'access-token' as const;

  // Track previous organization to detect changes
  const previousOrganizationId = useRef<string | null>(null);

  const candidateDataTransferred = useRef<boolean>(false);

  // event handlers
  const updateToken = async (): Promise<void> => {
    const token = await getToken({ template: TOKEN_TEMPLATE });
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token || '');
  };

  // effects
  useEffect(() => {
    if (organization) {
      window.localStorage.setItem('active-org-id', organization.id);
      void updateToken();
    }
  }, [organization, getToken]);

  useEffect(() => {
    if (isLoaded && !organization && userMemberships?.data?.length) {
      setActive({
        organization:
          window.localStorage.getItem('active-org-id') ||
          userMemberships.data[0].organization.id,
      });
    }
  }, [isLoaded, organization, userMemberships, setActive]);

  useEffect(() => {
    if (
      myProfileLoaded &&
      myProfileResult?.data?.id &&
      user?.emailAddresses?.[0]?.emailAddress &&
      organization?.id &&
      !candidateDataTransferred.current
    ) {
      candidateDataTransferred.current = true;

      logger.info(
        'Attempting to transfer consultant candidate data on dashboard load',
        {
          zor_id: organization.id,
          emailAddress: user.emailAddresses[0].emailAddress,
          consultantId: myProfileResult.data.id,
        }
      );

      transferCandidateData({
        zor_id: organization.id,
        emailAddress: user.emailAddresses[0].emailAddress,
        consultantId: myProfileResult.data.id,
      });
    }
  }, [
    myProfileLoaded,
    myProfileResult,
    user,
    organization,
    transferCandidateData,
  ]);

  useEffect(() => {
    if (transferSuccess) {
      logger.info('Consultant candidate data transfer completed successfully');
    }
  }, [transferSuccess]);

  // Handle organization switching - update token and reload page when organization changes
  useEffect(() => {
    if (organization?.id && isLoaded) {
      if (
        previousOrganizationId.current !== null &&
        previousOrganizationId.current !== organization.id
      ) {
        const updateTokenAndReload = async () => {
          try {
            const token = await getToken({ template: TOKEN_TEMPLATE });
            if (token) {
              window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
            }
          } catch (error) {
            logger.error(
              'Failed to update access token before reload',
              error as Error,
              {
                organizationId: organization?.id,
                previousOrganizationId: previousOrganizationId.current,
              }
            );
          } finally {
            window.location.reload();
          }
        };

        void updateTokenAndReload();
      }

      // Update the previous organization id
      previousOrganizationId.current = organization.id;
    }
  }, [organization?.id, isLoaded, getToken]);

  if (!organization && window.localStorage.getItem('access-token')) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <MainLayout>
      <Routes>
        <Route
          path="/"
          element={<Navigate to="/dashboard/data-center" replace />}
        />
        <Route path="/data-center" element={<DashboardDataCenter />} />
        <Route path="/profile" element={<DashboardProfile />} />
        <Route path="/library" element={<DashboardLibrary />} />
        <Route path="/territories" element={<DashboardTerritories />} />
        <Route path="/lead-profile/:id" element={<LeadProfile />} />
        <Route
          path="/financial-overview"
          element={<DashboardFinancialOverview />}
        />
        <Route path="/ask-aimei" element={<DashboardAskAimei />} />
        <Route path="/zee-profile/:id" element={<ZeeProfile />} />
      </Routes>
    </MainLayout>
  );
};
